import { useState, useEffect } from 'react';

export const useDeviceSafeArea = () => {
  const [safeAreaTop, setSafeAreaTop] = useState(0);

  useEffect(() => {
    const calculateSafeArea = () => {
      // Check if we're on a native platform (not web)
      // Capacitor Core can load on web, but we only want safe area on native platforms
      let isNativePlatform = false;
      let platform = 'web';
      
      try {
        const Capacitor = (window as any).Capacitor;
        if (Capacitor) {
          platform = Capacitor.getPlatform();
          // getPlatform() returns 'web' on browsers, 'ios' or 'android' on native
          isNativePlatform = platform !== 'web';
        }
      } catch (e) {
        // Not in Capacitor at all
        isNativePlatform = false;
        platform = 'web';
      }
      
      // If we're in a web browser (not native platform), don't apply safe area padding
      // The browser handles safe areas automatically
      if (!isNativePlatform) {
        setSafeAreaTop(0);
        return;
      }

      // Only calculate safe area for native Capacitor apps
      const testElement = document.createElement('div');
      testElement.style.position = 'fixed';
      testElement.style.top = '0';
      testElement.style.left = '0';
      testElement.style.width = '1px';
      testElement.style.height = '1px';
      testElement.style.paddingTop = 'env(safe-area-inset-top, 0px)';
      testElement.style.visibility = 'hidden';
      testElement.style.pointerEvents = 'none';
      document.body.appendChild(testElement);
      
      const computedStyle = window.getComputedStyle(testElement);
      const cssSafeArea = parseInt(computedStyle.paddingTop) || 0;
      document.body.removeChild(testElement);

      // If CSS safe area is available and > 0, use it
      if (cssSafeArea > 0) {
        setSafeAreaTop(cssSafeArea);
        return;
      }

      // Fallback: More reliable iOS detection for Capacitor apps
      const userAgent = navigator.userAgent;
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      
      if (isIOS) {
        // For iOS Capacitor apps, use more conservative safe area values
        const screenHeight = window.screen.height;
        const windowHeight = window.innerHeight;
        const screenWidth = window.screen.width;
        
        // Calculate the difference between screen and window height
        const systemUIHeight = screenHeight - windowHeight;
        
        // More accurate device detection for iOS
        const pixelRatio = window.devicePixelRatio || 1;
        const logicalWidth = screenWidth / pixelRatio;
        const logicalHeight = screenHeight / pixelRatio;
        
        // iPhone detection based on common resolutions
        if (logicalHeight >= 812 && logicalWidth >= 375) {
          // iPhone X and newer (with notch/Dynamic Island)
          setSafeAreaTop(44);
        } else if (logicalHeight >= 667 && logicalWidth >= 375) {
          // iPhone 6/7/8 Plus
          setSafeAreaTop(20);
        } else if (logicalHeight >= 568 && logicalWidth >= 320) {
          // iPhone 5/SE
          setSafeAreaTop(20);
        } else {
          // Other iOS devices
          setSafeAreaTop(Math.max(systemUIHeight, 20));
        }
        return;
      }
      
      // For non-iOS Capacitor apps, use minimal safe area
      setSafeAreaTop(0);
    };

    calculateSafeArea();
    
    // Recalculate on orientation change and resize
    window.addEventListener('resize', calculateSafeArea);
    window.addEventListener('orientationchange', calculateSafeArea);
    
    return () => {
      window.removeEventListener('resize', calculateSafeArea);
      window.removeEventListener('orientationchange', calculateSafeArea);
    };
  }, []);

  return safeAreaTop;
};
